"use client";

import React, { useRef, useState } from "react";
import { ChevronDown, Loader2 } from "lucide-react";
import {
  AddressHealth,
  AddressState,
  GatewayEndpoint,
  addressFieldMode,
  describeAddress,
  endpointKey,
  nextAddressIndex,
} from "@/lib/gateways";
import { useIsDesktop } from "@/hooks/use-mobile";
import { Popover, PopoverContent, PopoverTrigger } from "@/shadcn-components/ui/popover";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/shadcn-components/ui/drawer";
import { GatewayAddressList } from "./GatewayAddressList";

const FIELD_LABEL = "Gateway address";

const STATE_CLASS: Record<AddressState, string> = {
  checking: "text-muted-foreground",
  ready: "text-success",
  "not-ready": "text-muted-foreground",
  unreachable: "text-destructive",
};

const optionsIn = (root: HTMLElement | null) =>
  Array.from(root?.querySelectorAll<HTMLElement>('[role="radio"]') ?? []);

/**
 * The chosen gateway address as ONE form row — label on the left; `host:port`, its live state and
 * round trip on the right — for the Add Account credentials step and Edit Account. The full list
 * lives on the server step; here it only opens on demand: a popover under the row in the desktop
 * layout, a bottom sheet in the mobile one. A server with a single address shows it as plain text.
 */
export function GatewayAddressField({
  endpoints,
  health,
  selectedIndex,
  onSelect,
}: {
  endpoints: readonly GatewayEndpoint[];
  health: Readonly<Record<string, AddressHealth | undefined>>;
  selectedIndex: number;
  onSelect: (index: number) => void;
}) {
  const isDesktop = useIsDesktop();
  const [open, setOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  const mode = addressFieldMode(endpoints.length, selectedIndex);
  if (mode === "hidden") return null;

  const chosen = endpoints[selectedIndex];
  const summary = chosen ? describeAddress(chosen, health[endpointKey(chosen)]) : null;

  const value = (
    <span className="min-w-0 text-right">
      {summary ? (
        <>
          <span className="block font-mono text-[13px] text-foreground truncate">
            {summary.address}
          </span>
          <span className="mt-0.5 flex items-center justify-end gap-1.5 text-xs text-muted-foreground">
            <span className={`flex items-center gap-1.5 ${STATE_CLASS[summary.state]}`}>
              {summary.state === "checking" ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-current" />
              )}
              {summary.stateLabel}
            </span>
            {summary.latencyLabel ? <span>{`• ${summary.latencyLabel}`}</span> : null}
          </span>
        </>
      ) : (
        <span className="text-sm text-muted-foreground">choose an address</span>
      )}
    </span>
  );

  if (mode === "read-only") {
    return (
      <div className="flex items-center justify-between gap-3 p-3.5">
        <span className="text-sm font-medium shrink-0">{FIELD_LABEL}</span>
        {value}
      </div>
    );
  }

  const trigger = (
    <button
      type="button"
      className="group w-full flex items-center justify-between gap-3 p-3.5 text-left rounded-[inherit] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
    >
      <span className="text-sm font-medium shrink-0">{FIELD_LABEL}</span>
      <span className="flex items-center gap-2 min-w-0">
        {value}
        <ChevronDown
          aria-hidden
          className={`h-4 w-4 shrink-0 text-muted-foreground group-hover:text-foreground transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </span>
    </button>
  );

  /** Start on the chosen address (else the first), not on whatever happens to be first in the DOM. */
  const focusChosen = (event: Event) => {
    const options = optionsIn(listRef.current);
    const target = options[selectedIndex] ?? options[0];
    if (!target) return;
    event.preventDefault();
    target.focus();
  };

  const moveFocus = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const options = optionsIn(listRef.current);
    const current = options.indexOf(document.activeElement as HTMLElement);
    const next = nextAddressIndex(current, options.length, event.key);
    if (next === current) return;
    event.preventDefault();
    options[next]?.focus();
  };

  const list = (
    <div ref={listRef} onKeyDown={moveFocus}>
      <GatewayAddressList
        endpoints={endpoints}
        health={health}
        selectedIndex={selectedIndex}
        onSelect={(index) => {
          onSelect(index);
          setOpen(false); // Radix / vaul hand the focus back to the field
        }}
      />
    </div>
  );

  if (isDesktop) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent
          align="start"
          aria-label={FIELD_LABEL}
          onOpenAutoFocus={focusChosen}
          className="w-[var(--radix-popover-trigger-width)] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto p-2"
        >
          {list}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>{trigger}</DrawerTrigger>
      <DrawerContent className="max-h-[88vh] flex flex-col" onOpenAutoFocus={focusChosen}>
        <DrawerHeader className="pb-2 text-left">
          <DrawerTitle className="text-base">{FIELD_LABEL}</DrawerTitle>
          <DrawerDescription>
            {endpoints.length} gateway address{endpoints.length !== 1 ? "es" : ""}
          </DrawerDescription>
        </DrawerHeader>
        <div className="overflow-y-auto px-4 pb-6">{list}</div>
      </DrawerContent>
    </Drawer>
  );
}
